import {SupportedLocale, SupportedLocales} from '@multipass-i18n';
import {PlatformServiceClient} from '@multipass-platform-service';
import {KeyValueCache} from '@multipass-system';
import {Logger} from 'esp-js';
import {createInstance} from 'i18next';
import ICU from 'i18next-icu';
import {initReactI18next} from 'react-i18next';

import {I18nKey} from './i18nKey';

const _log = Logger.create('LocaleService');

const LOCALE_KEY = 'mp-locale';
const BASE_LOCALE = 'en';
const TRANSLATION_NS = 'translation';

export {SupportedLocales as SUPPORTED_LOCALES};
export type {SupportedLocale};

type I18nInstance = ReturnType<typeof createInstance>;

export const isSupportedLocale = (value: unknown): value is SupportedLocale =>
    (SupportedLocales as readonly string[]).includes(value as string);

/**
 * Best-match a BCP-47 browser/device language to a shipped locale: a `zh` tag carrying `tw`/`hant`
 * resolves to Traditional (`zh-TW`), any other `zh` (incl. a bare `zh` or `zh-CN`) to Simplified
 * (`zh-CN`); everything else to `en`.
 */
const resolveLocale = (language: string): SupportedLocale => {
    const lower = language.toLowerCase();
    if (lower.startsWith('zh')) {
        const traditional = lower.includes('hant') || lower.includes('tw');
        return traditional ? 'zh-TW' : 'zh-CN';
    }
    return 'en';
};

export class LocaleService {
    readonly #client: PlatformServiceClient;
    readonly #cache: KeyValueCache;
    readonly #i18n: I18nInstance;
    readonly #ready: Promise<unknown>;
    #locale: SupportedLocale;

    constructor(client: PlatformServiceClient, cache: KeyValueCache, navigatorLanguage: string) {
        this.#client = client;
        this.#cache = cache;
        this.#locale = this.#readLocale(navigatorLanguage);
        this.#i18n = createInstance();
        this.#ready = this.#i18n
            .use(ICU)
            .use(initReactI18next)
            .init({
                lng: this.#locale,
                fallbackLng: BASE_LOCALE,
                keySeparator: false,
                nsSeparator: false,
                interpolation: {escapeValue: false},
                react: {useSuspense: false},
                resources: {},
            });
    }

    public get currentLocale(): SupportedLocale {
        return this.#locale;
    }

    public get i18n(): I18nInstance {
        return this.#i18n;
    }

    public t(key: I18nKey, params?: Record<string, unknown>): string {
        return this.#i18n.t(key, params);
    }

    public tReason(code: string | null | undefined, fallback: string): string {
        return code ? this.#i18n.t(`system.${code}`, {defaultValue: fallback}) : fallback;
    }

    public get supportsStoredOverride(): boolean {
        return isSupportedLocale(this.#cache.getValue<SupportedLocale>(LOCALE_KEY));
    }

    public async setLocale(locale: SupportedLocale): Promise<void> {
        this.#cache.setValue(LOCALE_KEY, locale);
        await this.load(locale);
    }

    public async load(locale: SupportedLocale = this.#locale): Promise<void> {
        await this.#ready;
        if (this.#i18n.hasResourceBundle(locale, TRANSLATION_NS)) {
            await this.#i18n.changeLanguage(locale);
            this.#locale = locale;
            return;
        }
        try {
            const {entries} = await this.#client.requestGetI18nCatalog(locale);
            this.#i18n.addResourceBundle(locale, TRANSLATION_NS, entries, true, true);
            await this.#i18n.changeLanguage(locale);
            this.#locale = locale;
        } catch (error) {
            _log.error(`Failed to load i18n catalog for '${locale}'; rendering '${BASE_LOCALE}'/keys`, error);
        }
    }

    #readLocale(navigatorLanguage: string): SupportedLocale {
        const stored = this.#cache.getValue<SupportedLocale>(LOCALE_KEY);
        return isSupportedLocale(stored) ? stored : resolveLocale(navigatorLanguage);
    }
}
